import React, { useState, useContext, useEffect } from "react";
import { Form, Select, Tag, Spin } from "antd";
import { getFormItemLabel, getFormItemVaidation } from "../util/helpers";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import RestrictedContext from "../util/RestrictedContext";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import {
  listBranches,
  listEmployees,
  getEmployees,
  listPatients,
  listUsers,
  listCrmUsers,
  getBankBranches,
  getClientCategories,
  listClients,
  getRoles,
  getMetaData,
  getSettingsData,
  listSettingsData,
  getRestrictedSettingsData,
  listRestrictedSettingsData,
  getServiceTypes,
  listFeatures,
  getEmployeeEffectiveOrders,
  listPatientOrders,
  listSubProjects,
  getclientsTariffs,
} from "../../api/server";

import { getFullName } from "../util/helpers";
const { Option } = Select;

const AutoCompleteFormItem = (props) => {
  const {
    name,
    required,
    category,
    disabled,
    fetcher,
    params,
    extraParams,
    identfierId,
    getOptionLabel,
    items,
    paramName,
    hideLabel,
    onDataChanged,
    filterItems,
    filter,
    style,
    additionalRules,
    staleTime,
    ...rest
  } = props;

  const intl = useIntl();
  const [selectItems, setSelectItems] = useState([]);
  const [fetchedItems, setFetchedItems] = useState([]);
  const [updatedParams, setUpdatedParams] = useState(null);
  const [value, setValue] = useState();
  const [requestToken, setRequestToken] = useState(null);
  const restricted = useContext(RestrictedContext);
  let source = "no_source";
  if (params && params.name) {
    source = params.name.substring(params.name.indexOf("/") + 1);
  }
  const activeSource = useSelector((state) => state.sources[source]);

  useEffect(() => {
    let allParams = { ...params, ...extraParams };
    if (allParams.filter) {
      allParams.filter["freeText"] = value;
    }
    if (fetcher) {
      setUpdatedParams(allParams);
      setRequestToken(Math.random());
    }
  }, [value, params, extraParams, fetcher]);
  useEffect(() => {
    const identfier = identfierId ? identfierId : "id";
    const itemsIds = items && items.map((i) => i[identfier]);
    const final = items
      ? [
          ...items,
          ...fetchedItems.filter((i) => !itemsIds.includes(i[identfier])),
        ]
      : fetchedItems;
    const filtered = filterItems ? filterItems(final) : final;
    setSelectItems(filtered);
    if (onDataChanged) {
      onDataChanged(filtered);
    }
  }, [fetchedItems, filterItems, items]);

  const fetcherName = fetcher && fetcher.name;
  const { isLoading, data } = useQuery(
    [fetcherName, updatedParams, activeSource],
    async () => {
      const currentToken = requestToken;
      const result = await fetcher(updatedParams);
      if (currentToken !== requestToken) {
        throw new Error("Request out of sequence");
      }
      return result.data.items || [];
    },
    {
      keepPreviousData: true,
      staleTime: staleTime
        ? staleTime
        : updatedParams && updatedParams.filter
        ? 0
        : 1000 * 60 * 60 * 6, //6 hours
      enabled: fetcher !== null && updatedParams !== null,
      onError: (error) => {
        if (error.message !== "Request out of sequence") {
          console.error(error);
        }
      },
    }
  );

  useEffect(() => {
    if (data) {
      setFetchedItems(data);
    }
  }, [data]);

  const label = getFormItemLabel(intl, category, name, null);
  const arr = [];
  if (required) {
    arr.push("required");
  }
  let rules = getFormItemVaidation(intl, label, arr);
  if (additionalRules) {
    rules = [...rules, ...additionalRules];
  }
  const identfier = identfierId ? identfierId : "id";

  const tagRender = (props) => {
    const { label, closable, onClose } = props;
    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {label}
      </Tag>
    );
  };
  return (
    <Form.Item
      label={hideLabel ? null : label}
      name={name}
      rules={rules}
      style={style}
    >
      <Select
        {...rest}
        allowClear
        tagRender={tagRender}
        disabled={disabled || !restricted["hasWritePermission"]}
        showSearch={true}
        notFoundContent={isLoading ? <Spin size="small" /> : null}
        getPopupContainer={
          rest.getPopupContainer ? rest.getPopupContainer : undefined
        }
        onSearch={(newValue) => {
          setValue(newValue);
        }}
        onBlur={() => {
          setValue("");
        }}
        onChange={(value, options) => {
          if (rest.onChange) {
            rest.onChange(value, options);
          }
        }}
        value={value}
        filterOption={(input, option) => option.children.indexOf(input) >= 0}
      >
        {selectItems.map((option) => (
          <Option key={option[identfier]} value={option[identfier]}>
            {getOptionLabel
              ? getOptionLabel(option)
              : paramName && option[paramName]
              ? `${option.name} (${option[paramName]})`
              : option.name
              ? intl.formatMessage({
                  id: "app.dataSelect." + name + "." + option.name,
                  defaultMessage: option.name.toString(),
                })
              : ""}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

AutoCompleteFormItem.propTypes = {
  name: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  category: PropTypes.string,
  params: PropTypes.object,
  fetcher: PropTypes.func,
  onChange: PropTypes.func,
};

export default AutoCompleteFormItem;

export const MetaDataSelect = (props) => {
  const { metadataName, filterItems, ...rest } = props;

  return (
    <AutoCompleteFormItem
      fetcher={getMetaData}
      params={{ name: metadataName }}
      filterItems={filterItems}
      {...rest}
    />
  );
};

export const SettingsDataSelect = (props) => {
  const {
    settingsdataName,
    hideLabel,
    filterItems,
    waitForFetch,
    getOptionLabel,
    type,
    ...rest
  } = props;

  const fetcher = type && type === "list" ? listSettingsData : getSettingsData;
  const filter = type && type === "list" ? {} : null;
  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : fetcher}
      params={{ current: 1, name: settingsdataName, filter }}
      hideLabel={hideLabel}
      filterItems={filterItems}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const RestrictedSettingsDataSelect = (props) => {
  const {
    settingsdataName,
    hideLabel,
    filterItems,
    waitForFetch,
    getOptionLabel,
    type,
    ...rest
  } = props;

  const fetcher =
    type && type === "list"
      ? listRestrictedSettingsData
      : getRestrictedSettingsData;
  const filter = type && type === "list" ? {} : null;
  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : fetcher}
      params={{ current: 1, name: settingsdataName, filter }}
      hideLabel={hideLabel}
      filterItems={filterItems}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const SubProjectsSelect = (props) => {
  const { branchId, waitForFetch, ...rest } = props;
  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : listSubProjects}
      params={{ current: 1, pageSize: 100, branchId }}
      {...rest}
    />
  );
};

export const BranchSelect = (props) => (
  <AutoCompleteFormItem
    fetcher={listBranches}
    params={{ current: 1, pageSize: 100 }}
    {...props}
  />
);

export const EmployeesSelect = (props) => {
  const {
    waitForFetch,
    proffesional,
    showFullEmplyeeInfo,
    showEmplyeeId,
    ...rest
  } = props;
  const intl = useIntl();

  const getOptionLabel = (option) => {
    const {
      surename,
      first_name,
      local_id_number,
      active_branches,
      professional_role_id,
    } = option;
    const fullName = getFullName(surename, first_name);

    const parts = [
      fullName,
      local_id_number && `| ${local_id_number}`,
      active_branches?.length > 0 &&
        showFullEmplyeeInfo &&
        `| ${active_branches[0].name}`,
    ].filter(Boolean);

    let name =
      showFullEmplyeeInfo || showEmplyeeId ? parts.join(" ") : fullName;

    if (professional_role_id && proffesional) {
      const role = intl.formatMessage({
        id: `app.dataSelect.professional_role_id.${professional_role_id}`,
        defaultMessage: "",
      });
      name += `, ${role}`;
    }

    return name;
  };

  const filter = props.type && props.type === "list" ? {} : null;

  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : listEmployees}
      params={{
        current: 1,
        pageSize: 100,
        order: {
          field: "surename",
          order: "ascend",
        },
        filter,
      }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const NotAssociatedEmployeesSelect = (props) => {
  const { getEmployee, patientId, filterItems, ...rest } = props;

  const getOptionLabel = (option) => {
    return getFullName(option.surename, option.first_name);
  };
  return (
    <AutoCompleteFormItem
      items={getEmployee ? null : []}
      fetcher={listEmployees}
      filterItems={filterItems}
      params={{
        current: 1,
        pageSize: 100,
        query: {
          not_associated_only: true,
          associated_patient_id: patientId,
        },
      }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const BranchEmployeesSelect = (props) => {
  const { getEmployee, withId, items, ...rest } = props;
  const getOptionLabel = (option) => {
    return withId && option.local_id_number
      ? `${getFullName(option.surename, option.first_name)} (${
          option.local_id_number
        })`
      : getFullName(option.surename, option.first_name);
  };
  return (
    <AutoCompleteFormItem
      items={items ? items : getEmployee ? null : []}
      fetcher={listEmployees}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const BranchPatientsSelect = (props) => {
  const { getPatient, items, withId, ...rest } = props;

  const getOptionLabel = (option) => {
    return withId && option.local_id_number
      ? `${getFullName(option.surename, option.first_name)} (${
          option.local_id_number
        })`
      : getFullName(option.surename, option.first_name);
  };

  return (
    <AutoCompleteFormItem
      items={items ? items : getPatient ? null : []}
      fetcher={listPatients}
      params={{ current: 1, pageSize: 100 }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const UsersSelect = (props) => {
  const { getUser, items, ...rest } = props;

  const getOptionLabel = (option) => {
    return option.name;
  };
  return (
    <AutoCompleteFormItem
      items={getUser ? null : items ? items : []}
      fetcher={listUsers}
      params={{ current: 1, pageSize: 100 }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const CrmUsersSelect = (props) => {
  const { getUser, items, ...rest } = props;

  const getOptionLabel = (option) => {
    return option.name;
  };
  return (
    <AutoCompleteFormItem
      items={getUser ? null : items ? items : []}
      fetcher={listCrmUsers}
      params={{ current: 1, pageSize: 100 }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const FeaturesSelect = (props) => {
  const { getUser, ...rest } = props;
  const intl = useIntl();
  const getOptionLabel = (option) => {
    const featureName = intl.formatMessage({
      id: `app.dataSelect.features.${option.name}`,
    });
    return featureName;
  };
  return (
    <AutoCompleteFormItem
      fetcher={listFeatures}
      params={{ current: 1, pageSize: 100 }}
      getOptionLabel={getOptionLabel}
      {...rest}
    />
  );
};

export const BankBranchesSelect = (props) => {
  const getOptionLabel = (option) => {
    return `${option.name} (${option.branch_code})`;
  };
  return (
    <AutoCompleteFormItem
      fetcher={getBankBranches}
      {...props}
      getOptionLabel={getOptionLabel}
    />
  );
};
export const ClientCategoriesSelect = (props) => {
  const { waitForFetch, ...otherProps } = props;
  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : getClientCategories}
      {...otherProps}
    />
  );
};
export const ClientsSelect = (props) => {
  const { waitForFetch, ...otherProps } = props;
  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : listClients}
      params={{ current: 1, pageSize: 100 }}
      {...otherProps}
    />
  );
};
export const ServiceTypesSelect = (props) => {
  return <AutoCompleteFormItem fetcher={getServiceTypes} {...props} />;
};

export const RolesSelect = (props) => {
  return <AutoCompleteFormItem fetcher={getRoles} {...props} />;
};

export const EmployeeEffectiveOrderSelect = (props) => {
  const getOptionLabel = (option) => {
    const orderName = `${option.patient_surename} ${option.patient_first_name}, ${option.client_name}`;
    return orderName;
  };

  return (
    <AutoCompleteFormItem
      fetcher={getEmployeeEffectiveOrders}
      getOptionLabel={getOptionLabel}
      {...props}
    />
  );
};

export const PatientEffectiveOrderSelect = (props) => {
  const { waitForFetch, ...rest } = props;
  const getOptionLabel = (option) => {
    const orderName = `${option.patient.surename} ${option.patient.first_name}, ${option.client.name}`;
    return orderName;
  };

  return (
    <AutoCompleteFormItem
      fetcher={waitForFetch ? null : listPatientOrders}
      getOptionLabel={getOptionLabel}
      params={{ current: 1, pageSize: 100 }}
      {...rest}
    />
  );
};
export const ClientsTariffsSelect = (props) => {
  return <AutoCompleteFormItem fetcher={getclientsTariffs} {...props} />;
};
